import { useState, useEffect, useRef } from "react";
import { orgApi } from "../utils/apiV2";
import { Button } from "./ui/button";
import OnOffToggle from "./OnOffToggle";
import { 
  AlertTriangle, FileText, CreditCard, Plus, ChevronDown, Link2,
  Share2, Edit, Trash2, Package, ArrowRight, AlertCircle, Loader2, Check,
  LayoutGrid, List, Info
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "./ui/dialog";
import CreateServiceWizard from "./CreateServiceWizard";
import ServiceSharingModal from "./ServiceSharingModal";
import TabPageLayout from './TabPageLayout';
import EmptyState from './EmptyState';
import CardListViewToggle from './CardListViewToggle';
import { PrimaryButton, SecondaryButton, DangerButton } from './Buttons';
import StandardModal from './StandardModal';
import InfoCard from './InfoCard';
import { Badge } from "./ui/badge";

const ServicesTabImproved = ({ onNavigate }) => {
  const [services, setServices] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const addDropdownRef = useRef(null);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [viewMode, setViewMode] = useState("card");
  
  // Delete modal state
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, service: null, hasBookings: false, checkingBookings: false });
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Service sharing modal state
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [sharingService, setSharingService] = useState(null);
  
  // Business-critical gate checks
  const [hasWaiverText, setHasWaiverText] = useState(false);
  const [hasStripeConnect, setHasStripeConnect] = useState(false);
  const [gateCheckLoading, setGateCheckLoading] = useState(true);

  useEffect(() => {
    loadServices();
    loadAssets();
    checkGateRequirements();
  }, []);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addDropdownRef.current && !addDropdownRef.current.contains(event.target)) {
        setShowAddDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkGateRequirements = async () => {
    try {
      setGateCheckLoading(true);
      const orgResponse = await orgApi.getOrganization();
      const org = orgResponse.data;
      
      const waiverText = org.customWaiverText || '';
      setHasWaiverText(waiverText.trim().length >= 100);
      setHasStripeConnect(!!(org.stripeConnectAccountId && org.stripeConnectChargesEnabled));
    } catch (error) {
      console.error("Error checking gate requirements:", error);
    } finally {
      setGateCheckLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await orgApi.getServices({ activeOnly: false });
      
      // Map v2 response to component format
      const servicesData = response.data?.services || response.data || [];
      const mappedServices = servicesData.map(s => ({
        ...s,
        // Ensure legacy field names are available
        // 'active' is the Live/Off toggle, 'isActive' is for soft-delete/deactivation
        active: s.active ?? s.isActive,
        price: s.basePrice,
        duration: s.durationMinutes,
        sortOrder: s.displayOrder ?? 999
      }));
      
      // Sort by displayOrder to maintain correct order
      mappedServices.sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
      
      setServices(mappedServices);
    } catch (error) {
      console.error("Error loading services:", error);
      setError("Failed to load services");
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  const loadAssets = async () => {
    try {
      setAssetsLoading(true);
      const response = await orgApi.getAssets();
      const assetsData = response.data?.assets || response.data || [];
      setAssets(assetsData);
    } catch (error) {
      console.error("Error loading assets:", error);
    } finally {
      setAssetsLoading(false);
    }
  };

  const canReceivePayments = hasWaiverText && hasStripeConnect;

  const openServiceModal = async (service = null) => {
    // Allow creating/editing services even without Stripe/waiver
    // (they can demo the software, just can't receive payments)
    
    // If editing, fetch the full service data with ALL fields
    if (service) {
      try {
        const response = await orgApi.getService(service.id);
        setEditingService(response.data);
      } catch (error) {
        console.error('Error fetching service details:', error);
        toast.error('Failed to load service details');
        // Fall back to partial data
        setEditingService(service);
      }
    } else {
      setEditingService(null);
    }
    
    setShowServiceModal(true);
  };

  const handleDeleteService = async () => {
    if (!deleteConfirm.service) return;
    
    // If service is still Live, warn them to turn it off first
    if (deleteConfirm.service.active) {
      toast.error("Please turn the service 'Off' before deleting", { duration: 4000 });
      return;
    }
    
    setIsDeleting(true);
    const loadingToast = toast.loading(`Deleting ${deleteConfirm.service.name}...`);
    
    try {
      const response = await orgApi.deleteService(deleteConfirm.service.id);
      toast.dismiss(loadingToast);
      toast.success("Service deleted");
      setServices(prev => prev.filter(s => s.id !== deleteConfirm.service.id));
      setDeleteConfirm({ open: false, service: null, hasBookings: false, checkingBookings: false });
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error("Error deleting service:", error);
      const errorDetail = error.response?.data?.detail;
      
      // Check if error is about future bookings
      if (errorDetail?.includes('booking') || errorDetail?.includes('trips')) {
        toast.error("This service has upcoming bookings. Keep it turned off until all trips are completed, then you can delete it.", { duration: 6000 });
      } else {
        toast.error(errorDetail || "Failed to delete service", { duration: 6000 });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Open delete confirmation - check if service has future bookings
  const openDeleteConfirm = async (service) => {
    // If service is Live, warn them first
    if (service.active) {
      toast.info("Turn the service 'Off' first, then you can delete it", { duration: 4000 });
      return;
    }
    
    setDeleteConfirm({ open: true, service, hasBookings: false, checkingBookings: true });
    
    // Check for future bookings
    try {
      const response = await orgApi.getBookings({ 
        serviceId: service.id, 
        status: 'upcoming',
        limit: 1 
      });
      const hasUpcoming = response.data?.bookings?.length > 0 || response.data?.length > 0;
      setDeleteConfirm(prev => ({ ...prev, hasBookings: hasUpcoming, checkingBookings: false }));
    } catch (error) {
      console.error("Error checking bookings:", error);
      setDeleteConfirm(prev => ({ ...prev, checkingBookings: false }));
    }
  };

  // Track which service is being toggled
  const [togglingServiceId, setTogglingServiceId] = useState(null);

  const handleToggleServiceActive = async (service, newActiveState) => {
    setTogglingServiceId(service.id);
    const loadingToast = toast.loading(newActiveState ? `Turning ${service.name} Live...` : `Turning ${service.name} Off...`);
    
    try {
      // Only update 'active' field - this controls visibility in flows/widget
      // NOT 'isActive' which is the deactivation status
      await orgApi.updateService(service.id, {
        active: newActiveState
      });
      
      toast.dismiss(loadingToast);
      setServices(prev => prev.map(s => 
        s.id === service.id ? { ...s, active: newActiveState } : s
      ));
      
      toast.success(newActiveState ? `${service.name} is now Live` : `${service.name} is now Off`);
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error("Error toggling service:", error);
      toast.error("Failed to update service status");
      loadServices();
    } finally {
      setTogglingServiceId(null);
    }
  };

  // Error state
  if (error) {
    return (
      <TabPageLayout
        title="Services"
        subtitle="Manage bookable services"
      >
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Failed to load services</h3>
          <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
          <PrimaryButton onClick={() => loadServices()}>
            Try Again
          </PrimaryButton>
        </div>
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Services"
      subtitle="Manage bookable services"
      loading={loading}
      actions={
        <div className="bg-white rounded-lg p-1.5 shadow-[0_8px_24px_rgba(0,0,0,0.04)] border border-gray-100 flex items-center gap-1">
          {/* Card/List view toggle - inline */}
          <Button
            variant={viewMode === "card" ? "default" : "ghost"}
            size="sm"
            onClick={() => setViewMode("card")}
            style={viewMode === "card" ? { background: '#2563EB', color: 'white' } : { background: 'white', color: '#374151' }}
            className="h-8 w-8 p-0 flex items-center justify-center"
          >
            <LayoutGrid className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "ghost"}
            size="sm"
            onClick={() => setViewMode("list")}
            style={viewMode === "list" ? { background: '#2563EB', color: 'white' } : { background: 'white', color: '#374151' }}
            className="h-8 w-8 p-0 flex items-center justify-center"
          >
            <List className="w-4 h-4" />
          </Button>
          
          {/* Add Dropdown Button */}
          <div className="relative ml-1" ref={addDropdownRef}>
            <PrimaryButton 
              onClick={() => setShowAddDropdown(!showAddDropdown)} 
              className="h-8 px-3 gap-1" 
              disabled={assetsLoading || loading}
            >
              <Plus className="w-4 h-4" />
              Add
              <ChevronDown className={`w-3 h-3 transition-transform ${showAddDropdown ? 'rotate-180' : ''}`} />
            </PrimaryButton>
            
            {showAddDropdown && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                <button
                  onClick={() => {
                    setShowAddDropdown(false);
                    openServiceModal();
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4 text-gray-500" />
                  Create Service
                </button>
                <button
                  onClick={() => {
                    setShowAddDropdown(false);
                    onNavigate && onNavigate('partners');
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <Link2 className="w-4 h-4 text-blue-600" />
                  Link Shared Service
                </button>
              </div>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Setup Reminder Banner - shown when Stripe/waiver not complete */}
        {!gateCheckLoading && !canReceivePayments && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-800">Complete setup to receive payments</h3>
                <p className="text-sm text-blue-700 mt-1 mb-3">
                  You can create and preview services now! Complete these steps when you&apos;re ready to accept real bookings:
                </p>
                <div className="space-y-2">
                  {!hasWaiverText && (
                    <button
                      onClick={() => onNavigate && onNavigate('waivers')}
                      className="flex items-center gap-2 w-full p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-left"
                    >
                      <FileText className="w-5 h-5 text-blue-600" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Set up your waiver</p>
                        <p className="text-xs text-gray-600">Add waiver text (100+ characters) to protect your business</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                  {!hasStripeConnect && (
                    <button
                      onClick={() => onNavigate && onNavigate('payments')}
                      className="flex items-center gap-2 w-full p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-left"
                    >
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Connect Stripe</p>
                        <p className="text-xs text-gray-600">Set up payment processing to accept bookings</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Services Content */}
        {services.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No Services Yet"
            description="Create your first service to start accepting bookings"
            actionLabel="Create Service"
            onAction={() => openServiceModal()}
          />
        ) : viewMode === "card" ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service, index) => {
              const isLive = service.active === true;
              
              return (
                <div 
                  key={service.id} 
                  className={`bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden transition-all relative group hover:shadow-lg`}
                >
                  {/* Image */}
                  <div className="relative h-40 sm:h-48 bg-gray-100">
                    {service.images && service.images.length > 0 ? (
                      <img 
                        src={service.images[service.mainImageIndex || 0]} 
                        alt={service.name} 
                        className={`w-full h-full object-cover ${!isLive ? 'opacity-70' : ''}`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-12 h-12 text-gray-300" />
                      </div>
                    )}
                    
                    {/* Live Toggle */}
                    <div 
                      className="absolute top-2 right-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {togglingServiceId === service.id ? (
                        <div className="flex items-center justify-center px-3 py-2 rounded-full bg-white/90 backdrop-blur-sm shadow-sm">
                          <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                        </div>
                      ) : (
                        <>
                          {/* Mobile: Plain toggle without background */}
                          <div className="sm:hidden">
                            <OnOffToggle
                              value={isLive}
                              onChange={(checked) => handleToggleServiceActive(service, checked)}
                              disabled={togglingServiceId !== null}
                            />
                          </div>
                          {/* Desktop: Toggle with centered pill background */}
                          <div className="hidden sm:flex items-center justify-center px-3 py-2 rounded-full bg-white/90 backdrop-blur-sm shadow-sm min-w-[52px]">
                            <OnOffToggle
                              value={isLive}
                              onChange={(checked) => handleToggleServiceActive(service, checked)}
                              disabled={togglingServiceId !== null}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-semibold text-lg mb-1 text-gray-900">{service.name}</h3>
                    {service.tagline && (
                      <p className="text-sm text-gray-600 mb-2">{service.tagline}</p>
                    )}
                    {service.description && (
                      <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                        {service.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                      <span className="font-semibold text-blue-600">
                        {service.currency === 'AED' ? 'AED ' : '$'}{service.price || 0}
                      </span>
                      {service.duration && <span>{service.duration}min</span>}
                      {service.capacity && <span>{service.capacity} max</span>}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      <SecondaryButton
                        size="sm"
                        onClick={() => openServiceModal(service)}
                        className="flex-1"
                      >
                        <Edit className="w-3.5 h-3.5 mr-1" />
                          Edit
                        </SecondaryButton>
                        <SecondaryButton
                          size="sm"
                          onClick={() => {
                            setSharingService(service);
                            setShowSharingModal(true);
                          }}
                          className={service.sharing?.enabled ? 'text-green-600 border-green-300 bg-green-50' : ''}
                          title={service.sharing?.enabled ? 'Sharing enabled' : 'Configure sharing'}
                        >
                          <Share2 className="w-3.5 h-3.5" />
                        </SecondaryButton>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteConfirm(service)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        ) : (
          <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {services.map((service) => {
                    const isLive = service.active === true;
                    
                    return (
                      <tr key={service.id} className="transition-colors hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {service.images && service.images.length > 0 ? (
                              <img 
                                src={service.images[service.mainImageIndex || 0]} 
                                alt={service.name} 
                                className={`w-12 h-12 rounded-lg object-cover ${!isLive ? 'opacity-60' : ''}`}
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                                <Package className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-gray-900">
                                {service.name}
                              </div>
                              {service.tagline && (
                                <div className="text-xs text-gray-500 hidden sm:block">{service.tagline}</div>
                              )}
                              <div className="text-xs text-gray-600 sm:hidden">
                                {service.currency === 'AED' ? 'AED ' : '$'}{service.price} • {service.duration}min
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="font-semibold text-blue-600">
                            {service.currency === 'AED' ? 'AED ' : '$'}{service.price || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
                          {service.duration ? `${service.duration} min` : '-'}
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <div className="flex items-center gap-2">
                            {togglingServiceId === service.id ? (
                              <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                            ) : (
                              <OnOffToggle
                                value={isLive}
                                onChange={(checked) => handleToggleServiceActive(service, checked)}
                                disabled={togglingServiceId !== null}
                              />
                            )}
                            <Badge variant="outline" className={isLive ? 'border-blue-500 text-blue-600' : 'border-gray-300 text-gray-500'}>
                              <Check className="w-3 h-3 mr-1" />
                              {isLive ? 'Live' : 'Off'}
                            </Badge>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openServiceModal(service)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSharingService(service);
                                setShowSharingModal(true);
                              }}
                              className={service.sharing?.enabled ? 'text-green-600 hover:bg-green-50' : 'text-gray-600 hover:bg-gray-100'}
                            >
                              <Share2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteConfirm(service)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Service Modal */}
        <Dialog open={showServiceModal} onOpenChange={setShowServiceModal}>
          <DialogContent className="max-w-xl max-h-[95vh] p-6">
            <CreateServiceWizard
              editingService={editingService}
              onComplete={(savedService) => {
                loadServices();
                setShowServiceModal(false);
                setEditingService(null);
              }}
              onCancel={() => {
                setShowServiceModal(false);
                setEditingService(null);
              }}
              assets={assets}
            />
          </DialogContent>
        </Dialog>
        
        {/* Service Sharing Modal */}
        <ServiceSharingModal
          isOpen={showSharingModal}
          onClose={() => {
            setShowSharingModal(false);
            setSharingService(null);
          }}
          service={sharingService}
          onUpdate={loadServices}
        />
        
        {/* Delete Confirmation Modal */}
        <StandardModal
          isOpen={deleteConfirm.open}
          onClose={() => setDeleteConfirm({ open: false, service: null, hasBookings: false, checkingBookings: false })}
          title={deleteConfirm.hasBookings ? "Can't Delete Yet" : "Delete Service?"}
          description={deleteConfirm.hasBookings 
            ? `"${deleteConfirm.service?.name}" has upcoming trips` 
            : `Are you sure you want to delete "${deleteConfirm.service?.name}"?`}
          size="sm"
          footer={
            deleteConfirm.checkingBookings ? (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : deleteConfirm.hasBookings ? (
              <SecondaryButton
                onClick={() => setDeleteConfirm({ open: false, service: null, hasBookings: false, checkingBookings: false })}
                className="w-full"
              >
                Got it
              </SecondaryButton>
            ) : (
              <>
                <SecondaryButton
                  onClick={() => setDeleteConfirm({ open: false, service: null, hasBookings: false, checkingBookings: false })}
                  className="flex-1"
                  disabled={isDeleting}
                >
                  Cancel
                </SecondaryButton>
                <DangerButton
                  onClick={handleDeleteService}
                  className="flex-1"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Service'
                  )}
                </DangerButton>
              </>
            )
          }
        >
          {deleteConfirm.checkingBookings ? (
            <p className="text-gray-500 text-center py-4">Checking for upcoming bookings...</p>
          ) : deleteConfirm.hasBookings ? (
            <InfoCard variant="warning" showIcon={true}>
              <p className="font-medium mb-1">This service has upcoming trips.</p>
              <p>Keep it turned &quot;Off&quot; until all trips are completed, then you can delete it.</p>
            </InfoCard>
          ) : (
            <InfoCard variant="danger" showIcon={true}>
              <p className="font-medium mb-1">This action cannot be undone.</p>
              <p>This service will no longer be bookable. Existing bookings will remain.</p>
            </InfoCard>
          )}
        </StandardModal>
      </div>
    </TabPageLayout>
  );
};

export default ServicesTabImproved;
